import {
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithPopup,
  signOut,
  User,
} from "firebase/auth";
import Cookies from "js-cookie";
import { create } from "zustand";
import { UserApi } from "../api/userApi";
import { auth, googleProvider } from "../config/firebase_config";

interface AuthActions {
  initialize: () => void;
  signInWithGoogle: () => Promise<void>;
  hasAccessToProject: (projectId: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  dispose: () => void;
}

interface AuthState {
  user: User | null;
  isInitialized: boolean;
  hasUserManuallySignedOut: boolean;

  actions: AuthActions;
}

export const useAuthStore = create<AuthState>((set) => {
  let unsubscribeAuthStateChanged: (() => void) | null = null;
  let unsubscribeIdTokenChanged: (() => void) | null = null;

  return {
    user: null,
    isInitialized: false,
    hasUserManuallySignedOut: false,

    actions: {
      initialize: () => {
        unsubscribeAuthStateChanged = onAuthStateChanged(auth, (user) => {
          set({
            user: user,
            isInitialized: true,
          });
        });
        unsubscribeIdTokenChanged = onIdTokenChanged(auth, async (user) => {
          if (user) {
            const authTokenData = await user.getIdTokenResult();
            Cookies.set("session", authTokenData.token, {
              expires: new Date(authTokenData.expirationTime),
              secure: true,
              sameSite: "strict",
            });
          }
        });
      },

      signInWithGoogle: async () => {
        const result = await signInWithPopup(auth, googleProvider);
        const user = result.user;

        if (!user) {
          throw new Error("No user found after sign in.");
        }

        await UserApi.initUser();

        set({
          user: user,
          hasUserManuallySignedOut: false,
        });
      },

      hasAccessToProject: async (projectId: string) => {
        return await UserApi.hasAccessToProject(projectId);
      },

      signOut: async () => {
        set({
          hasUserManuallySignedOut: true,
        });
        localStorage.removeItem("projects-history");
        try {
          await signOut(auth);
          set({
            user: null,
          });
        } catch (error) {
          console.error("Error signing out:", error);
          throw error;
        }
      },

      dispose: () => {
        if (unsubscribeAuthStateChanged) {
          unsubscribeAuthStateChanged();
          unsubscribeAuthStateChanged = null;
        }
        if (unsubscribeIdTokenChanged) {
          unsubscribeIdTokenChanged();
          unsubscribeIdTokenChanged = null;
        }
      },
    },
  };
});

export const useIsAuthInitialized = () =>
  useAuthStore((state) => state.isInitialized);

export const useIsUserSignedIn = () =>
  useAuthStore((state) => state.user !== null);

export const useAuthUser = () => useAuthStore((state) => state.user);

export const useHasUserManuallySignedOut = () =>
  useAuthStore((state) => state.hasUserManuallySignedOut);

export const useAuthActions = () => useAuthStore((state) => state.actions);
